import {
  AlertCircle,
  CheckCircle2,
  CircleHelp,
  RotateCcw,
  Target,
  Trophy
} from "lucide-react";
import { birds } from "../data/birds";
import type { ProgressState } from "../hooks/useProgress";

type ProgressViewProps = {
  progress: ProgressState;
  stats: {
    known: number;
    fuzzy: number;
    learning: number;
    today: number;
    streak: number;
  };
  onReview: () => void;
  onReset: () => void;
};

export const ProgressView = ({
  progress,
  stats,
  onReview,
  onReset
}: ProgressViewProps) => {
  const knownPercent = Math.round((stats.known / birds.length) * 100);
  const studied = stats.known + stats.fuzzy + stats.learning;

  const confirmReset = () => {
    if (window.confirm("确定清空全部学习记录吗？此操作无法撤销。")) {
      onReset();
    }
  };

  return (
    <main className="page progress-page">
      <header className="page-heading">
        <div>
          <span className="section-kicker">学习进度</span>
          <h1>每一次认出，都会留在这里</h1>
          <p>数据只保存在当前浏览器，不需要注册账号。</p>
        </div>
      </header>

      <section className="progress-overview">
        <article className="mastery-card">
          <div
            className="mastery-orbit"
            style={{ "--progress": `${knownPercent * 3.6}deg` } as React.CSSProperties}
          >
            <span>
              <strong>{stats.known}</strong>
              / {birds.length}
            </span>
          </div>
          <div>
            <span className="section-kicker">已认识</span>
            <h2>{knownPercent}% 的图卡已经掌握</h2>
            <p>已看过 {studied} 张，还有 {birds.length - studied} 张等待第一次见面。</p>
          </div>
        </article>
        <div className="progress-stat-grid">
          <article>
            <CheckCircle2 size={22} />
            <strong>{stats.known}</strong>
            <span>已认识</span>
          </article>
          <article>
            <AlertCircle size={22} />
            <strong>{stats.fuzzy}</strong>
            <span>待巩固</span>
          </article>
          <article>
            <CircleHelp size={22} />
            <strong>{stats.learning}</strong>
            <span>学习中</span>
          </article>
          <article>
            <Trophy size={22} />
            <strong>{stats.streak}</strong>
            <span>连续天数</span>
          </article>
        </div>
      </section>

      <section className="progress-actions">
        <article>
          <span className="progress-actions__icon">
            <Target size={23} />
          </span>
          <div>
            <h2>今天已学习 {stats.today} 张</h2>
            <p>
              每日目标 {progress.dailyGoal} 张，
              {stats.today >= progress.dailyGoal
                ? "今天已经完成。"
                : `还差 ${progress.dailyGoal - stats.today} 张。`}
            </p>
          </div>
        </article>
        <button
          className="button button--ink"
          onClick={onReview}
          disabled={
            !progress.mistakes.length && !stats.fuzzy && !stats.learning
          }
        >
          复习薄弱鸟类
        </button>
      </section>

      <section className="danger-zone">
        <div>
          <RotateCcw size={20} />
          <span>
            <strong>重新开始</strong>
            清除认识程度、错题和连续学习记录
          </span>
        </div>
        <button type="button" onClick={confirmReset}>
          清空记录
        </button>
      </section>
    </main>
  );
};
